import { Component, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDivider } from '@angular/material/divider';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { IconButtonComponent, IconComponent } from '@claas/claas-ui-components';
import { getLatestNews, updateLatestNewsLastSeen } from '@farmnet/platform-tools-lib-fe';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { User } from 'cde-fe-organization-registration-dialog';
import { CustomDimension } from '@cde-fe-app/app/shared/models/event-tracking.model';
import { Features } from '@cde-fe-app/app/shared/models/feature-toggle.model';
import { ICurrentOrganisation } from '@cde-fe-app/app/shared/models/organisation.model';
import { EventTrackingService } from '@cde-fe-app/app/shared/services/event-tracking.service';
import { IState } from '@cde-fe-app/app/shared/state';
import { getTokenSelector } from '@cde-fe-app/app/shared/state/auth';
import { openHelpCenter } from '@cde-fe-app/app/shared/utils/open-help-center';
import { environment } from '@cde-fe-app/environments/environment';

@Component({
  selector: 'app-help-menu',
  templateUrl: './help-menu.component.html',
  styleUrls: ['./help-menu.component.scss'],
  imports: [IconButtonComponent, IconComponent, MatDivider, MatMenu, MatMenuItem, MatMenuTrigger, TranslateModule],
})
export class HelpMenuComponent {
  readonly currentUser = input<User | null>(null);
  readonly currentOrganisation = input<ICurrentOrganisation | null>(null);
  protected readonly features = Features;
  protected showNewsBadge = signal(false);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly store = inject<Store<IState>>(Store<IState>);
  private readonly accessToken = toSignal(this.store.select(getTokenSelector), { initialValue: '' });
  private router = inject(Router);

  constructor() {
    effect(() => {
      const user = this.currentUser();
      const token = this.accessToken();

      if (user && token) {
        getLatestNews(user.uuid, token, { baseUrl: environment.farmConnectBaseUrl + 'api' })
          .then(response => this.showNewsBadge.set(response.unseenNews))
          .catch(error => {
            console.error('Error fetching latest news:', error);
          });
      }
    });
  }

  onHelpMenuOpen(): void {
    this.eventTrackingService.eventTrackNew({
      payload: {
        event: 'machinemgtevent',
        eventCategory: 'Machine Management Header',
        eventAction: 'Click Dropdown',
        eventName: 'open help selector',
      },
    });
  }

  onHelpCenterClick(): void {
    this.trackHelpFunctionClick('Help Center');
    openHelpCenter(this.currentUser()?.user_metadata.language);
  }

  onSupportFormClick(): void {
    this.trackHelpFunctionClick('Support');
    void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      void this.router.navigate(['support']);
    });
  }

  onNewsClick(): void {
    this.trackHelpFunctionClick('News');
    const helpCenterUrl = `${environment.helpCenterBaseUrl}/${this.currentUser()?.user_metadata.language ?? 'de'}/news`;
    const currentUser = this.currentUser();
    const token = this.accessToken();
    if (currentUser && token) {
      this.showNewsBadge.set(false);
      updateLatestNewsLastSeen(currentUser.uuid, new Date(), token, {
        baseUrl: environment.farmConnectBaseUrl + 'api',
      }).catch(() => {
        this.showNewsBadge.set(true);
      });
    }
    window.open(helpCenterUrl, '_blank');
  }

  onInviteTempSupportClick(): void {
    this.trackHelpFunctionClick('Invite Support');
    const orgId = this.currentOrganisation()?.auth0Id;
    if (!orgId) {
      return;
    }

    const url = `${environment.claasIdDashboardBaseUrl}/organisation-data?orgId=${orgId}&selectedTab=members&startInvite=true&preselectedRole=TEMPORARY_SUPPORT`;
    window.open(url, '_blank');
  }

  private trackHelpFunctionClick(functionName: string): void {
    this.eventTrackingService.eventTrackNew({
      payload: {
        event: 'machinemgtevent',
        eventCategory: 'Machine Management Header',
        eventAction: 'Click Button',
        eventName: 'Select help function',
        customDimensions: { [CustomDimension.EVENT_LABEL]: functionName },
      },
    });
  }
}
